import { Component, OnInit } from "@angular/core";
import { ColDef, ColumnState, DateFilterModel, SetFilterModel, TextFilterModel } from "ag-grid-community";
import { defaultColumnTemplate, sharedColNames } from '../../../create-request/components/transactions-table/table-helpers';
import { ActionsLinkComponent } from "../../../shared/actions-link/actions-link.component";
import { select, Store } from "@ngrx/store";
import { selectMasterAccount } from "../../../account-info-header/reducers";
import { filter, map, Observable, Subject, takeUntil, withLatestFrom } from "rxjs";
import { gridReady } from "../../actions/sa-master-status-table-actions";
import { Constants, masterStandingAuthTableTabName, MsvTransactionTypeRemaps, transactionTypes } from "../../../shared/utilities/Constants";
import { StandingAuthMasterStatusTableActions, StandingAuthMasterStatusViewActions } from "../../actions";
import { selectMasterLettersData, selectMasterStandingAuthTableState } from "../../reducers";
import { MsvLetterAttributes } from "../../../create-request/models/Standing-Authorizations/LettersResponse";
import { MasterStandingAuthTableColumn } from '../../../shared/types/MasterStandingAuthTableColumn';
import { NamePipe } from "../../../shared/pipes/NamePipe";
import { TableColumn } from "../../../shared/types/TableColumn";
import { TableComponentState } from "../../../shared/types/TableComponentState";
import { agFilterNames } from "../../../shared/types/AgFilterNames";
import { AccountCellRendererComponent } from "../../../shared/components/account-cell-renderer/account-cell-renderer.component";
import { selectMsvAllTab, selectPreferencesLoaded } from "../../../user-preference/reducers";

@Component({
  standalone: false,
  selector: 'mm-sa-master-status-table',
  templateUrl: './sa-master-status-table.component.html',
  styleUrls: ['./sa-master-status-table.component.scss'],
})
export class SaMasterStatusTableComponent implements OnInit {
  noExportCol: string[] = ['Actions'];
  columnNames = sharedColNames;
  actionsColumnDef: ColDef[] = [
    {
      field: this.columnNames.actions,
      sortable: false,
      cellRenderer: ActionsLinkComponent,
      cellRendererParams: {
        onClick: this.actionLinkClicked.bind(this),
        viewStatus: 'View',
      },
      minWidth: 120,
      maxWidth: 120,
      flex: 1,
    },
  ];
  defaultMasterStandingAuthTableColumnDefs: ColDef[] = [
    {
      field: this.columnNames.accountNumber,
      sort: 'asc',
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      flex: 1,
      minWidth: 130,
      cellRenderer: AccountCellRendererComponent,
    },
    {
      field: this.columnNames.accountName,
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      flex: 1,
      minWidth: 200,
    },
    {
      field: this.columnNames.registrationType,
      filter: agFilterNames.set,
      floatingFilterComponent: 'customSetFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      flex: 1,
      minWidth: 130,
    },
    {
      field: this.columnNames.transactionType,
      filter: agFilterNames.set,
      floatingFilterComponent: 'customSetFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      flex: 1,
      minWidth: 120,
      width: 200,
    },
    {
      field: this.columnNames.direction,
      filter: agFilterNames.set,
      floatingFilterComponent: 'customSetFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      flex: 1,
      minWidth: 80,
    },
    {
      field: this.columnNames.toFromAccount,
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      flex: 1,
      minWidth: 120,
      width: 335,
    },
  ];
  filterModel: {
    [key: string]: TextFilterModel | DateFilterModel | SetFilterModel;
  };
  currentSelectedTabDefaults: ColDef[] = [];
  masterStandingAuthColumnDefs: ColDef[] = [];
  currentTab: string;
  destroy$ = new Subject<void>();
  accountNumber: string;
  fileName: string;
  isTooManyRecords: boolean;
  rowData: TableColumn[];
  masterStatusTableState$: Observable<TableComponentState>;
  atpReturnLimitMessage: string = Constants.atpSaReturnLimitMessage;

  constructor(private store: Store, private namePipe: NamePipe) {}

  ngOnInit(): void {
    this.currentSelectedTabDefaults = this.actionsColumnDef.concat(
      this.defaultMasterStandingAuthTableColumnDefs
    );
    this.masterStandingAuthColumnDefs = this.actionsColumnDef.concat(
      this.defaultMasterStandingAuthTableColumnDefs
    );

    this.currentTab = 'ALL';

    this.masterStatusTableState$ = this.store
      .select(selectMasterStandingAuthTableState)
      .pipe(takeUntil(this.destroy$));

    this.store.select(selectPreferencesLoaded)
    .pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectMsvAllTab)
      ),
      filter(([loaded]) => loaded === true)
    ).subscribe(([_loaded, tab]) => {
      if (tab && tab.columns?.length !== 0) {
        this.masterStandingAuthColumnDefs = this.buildColumnPreferences(tab.columns, tab.tabName);
      }
    });

    this.store
      .select(selectMasterAccount)
      .pipe(
        takeUntil(this.destroy$),
        filter((accountNumber) => !!accountNumber && accountNumber !== this.accountNumber) // Ensure masterAccount is not null or undefined
      )
      .subscribe((accountNumber) => {
        this.accountNumber = accountNumber.padStart(8, '0');
        this.fileName = `MoveMoneyMasterStandingAuthTxnList_${this.accountNumber}_`;

        // Dispatch getMasterTransactionsList after masterAccount is available
        this.store.dispatch(StandingAuthMasterStatusViewActions.callLetters());
      });

    this.store
      .select(selectMasterLettersData)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((letters) => {
        this.isTooManyRecords = letters?.length > 0 ? letters?.at(0)?.isTooManyRecords ?? false : false;
      });

    this.store
      .select(selectMasterLettersData)
      .pipe(
        map((letters) => this.mapLettersDataToRowData(letters || [])),
        takeUntil(this.destroy$)
      )
      .subscribe((letters) => {
        this.rowData = letters;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  actionLinkClicked(event: any) {
    // TO-DO for future actions
    if (event.name === 'View') {
      this.statusClicked(event);
    }
  }

  statusClicked(event: any) {
    this.store.dispatch(
      StandingAuthMasterStatusTableActions.loadViewPage({
        viewType: 'master-standing-auth',
      })
    );
    this.callTransactionInquiry(event);
  }

  callTransactionInquiry(event: any) {
    this.store
      .pipe(
        takeUntil(this.destroy$),
        select(selectMasterLettersData),
        map((element: any) =>
          element.find((letter: any) => {
            return (
              letter.cashieringRqstId ===
              event.rowData['CashieringRqstId']
            );
          })
        ),
        filter((res) => res !== undefined)
      )
      .subscribe((res) => {
        if (event.rowData.Type.includes('Internal Transfer')) {
          this.store.dispatch(
            StandingAuthMasterStatusViewActions.callSaMsvInternalTransferInquiry({
              payload: res,
              viewType: 'standing-auth',
              transactionType: transactionTypes.internalTransfer,
            })
          );
        }
        else if (event.rowData.Type.includes('Check')) {
          this.store.dispatch(StandingAuthMasterStatusViewActions.callSaMsvCheckInquiry({
            payload: res,
            viewType: 'standing-auth',
            transactionType: transactionTypes.check
          }));
        }
        else if (event.rowData.Type.includes('ACH')) {
          this.store.dispatch(StandingAuthMasterStatusViewActions.callSaMsvAchInquiry({
            payload: res,
            viewType: 'standing-auth',
            transactionType: transactionTypes.ach
          }));
          }
          else if (event.rowData.Type.includes('Wire')) {
          this.store.dispatch(StandingAuthMasterStatusViewActions.callSaMsvWireInquiry({
            payload: res,
            viewType: 'standing-auth',
            transactionType: transactionTypes.wireSloa,
            resourceVersion: '3'
          }));
        }
      });
  }

  updateGridReady(ready: boolean) {
    this.store.dispatch(
      gridReady({
        gridReady: ready,
        tab: this.currentTab as masterStandingAuthTableTabName,
      })
    );
  }

  mapLettersDataToRowData(
    letters: MsvLetterAttributes[]
  ): MasterStandingAuthTableColumn[] {
    return letters?.map((letter) => {
      return this.mapLetter(letter);
    });
  }

  mapLetter(letter: MsvLetterAttributes): MasterStandingAuthTableColumn {
    return {
      'Account number': letter.formattedAccountNumber,
      'Account name': letter.accountName ?? '',
      'Registration type': letter.registrationType ?? '',
      'Type': `${this.mapRedesignCompliantTransactionType(letter.typeDescription)} - ${this.mapPartyType(letter)}${letter.bundledSloaIn ? ' - Bundled' : ''}`,
      'Direction': letter.outIn,
      'To/From': this.namePipe.transform(
        `${letter.toForStatusAndRecurring}`
      ),
      'CashieringRqstId': letter.cashieringRqstId,
      'CashieringRqstSecondId': letter.cashieringRqstSecondId
    };
  }

  mapRedesignCompliantTransactionType(typeDescription: string): string {
    if (typeDescription === 'Journal') {
      return MsvTransactionTypeRemaps.internalTransfer;
    } else if (typeDescription === 'MoneyLink') {
      return MsvTransactionTypeRemaps.ach;
    } else {
      return typeDescription;
    }
  }

  mapPartyType(letter: MsvLetterAttributes): string {
    if (letter.thirdPartyIndValue === 'I') {
      return 'No IA Authority';
    }
    if (letter.isThirdPartyInd) {
      return '3rd Party';
    } else {
      return '1st Party';
    }
  }

  buildColumnPreferences(preferences: ColumnState[], tabName: string): ColDef[] {
      let updatedPreferences: ColumnState[] = [];
      preferences.forEach(preference => {
        if (isNaN(Number(preference.colId))) {
          updatedPreferences.push(preference);
        }
      });

      let result: ColDef[] = updatedPreferences.map((preference) => {
        switch (preference.colId) {

          case 'Actions':
            return {
              field: this.columnNames.actions,
              sortable: false,
              filter: agFilterNames.set,
              floatingFilter: false,
              cellRenderer: ActionsLinkComponent,
              cellRendererParams: {
                onClick: this.actionLinkClicked.bind(this),
                viewStatus: 'View'
              },
              minWidth: 120,
              maxWidth: 120,
              flex: 1,
            }

          case 'Account number':
            return {
              field: this.columnNames.accountNumber,
              sortIndex: tabName === 'transaction status' ? 0 : tabName === 'recurring' ? 1 : null,
              sort: preference.sort,
              floatingFilterComponent: 'customTextFloatingFilter',
              floatingFilterComponentParams: {
                suppressFilterButton: true,
              },
              headerComponentParams: { template: defaultColumnTemplate },
              flex: 1,
              minWidth: 140,
              cellRenderer: AccountCellRendererComponent,
              headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
            }

          case 'Account name':
            return {
              field: this.columnNames.accountName,
              sort: preference.sort,
              floatingFilterComponent: 'customTextFloatingFilter',
              floatingFilterComponentParams: {
                suppressFilterButton: true,
              },
              headerComponentParams: { template: defaultColumnTemplate },
              minWidth: 140,
              headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
            }

          case 'Registration type':
            return {
              field: this.columnNames.registrationType,
              filter: agFilterNames.set,
              sort: preference.sort,
              floatingFilterComponent: 'customSetFloatingFilter',
              floatingFilterComponentParams: {
                suppressFilterButton: true,
              },
              headerComponentParams: { template: defaultColumnTemplate },
              flex: 1,
              minWidth: 120,
              headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
            }

          case '':
            return {
              field: '',
              sortable: false,
              filter: false,
              minWidth: 160,
              maxWidth: 160
            }

          case 'Type':
            return {
              field: this.columnNames.transactionType,
              filter: agFilterNames.set,
              sort: preference.sort,
              floatingFilterComponent: 'customSetFloatingFilter',
              floatingFilterComponentParams: {
                suppressFilterButton: true,
              },
              headerComponentParams: { template: defaultColumnTemplate },
              flex: 1,
              minWidth: 120,
              headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
            }

          case 'To/From':
            return {
              field: this.columnNames.toFromAccount,
              sort: preference.sort,
              floatingFilterComponent: 'customTextFloatingFilter',
              floatingFilterComponentParams: {
                suppressFilterButton: true,
              },
              headerComponentParams: { template: defaultColumnTemplate },
              filterParams: {
                textFormatter: (value: string) => value == null ? null : value.toLowerCase().replaceAll('-', '')
              },
              flex: 1,
              minWidth: 120,
              headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
            }

          case 'Direction':
            return {
              field: this.columnNames.direction,
              sort: preference.sort,
              flex: 1,
              floatingFilterComponent: 'customSetFloatingFilter',
              floatingFilterComponentParams: {
                suppressFilterButton: true,
              },
              headerComponentParams: { template: defaultColumnTemplate },
              filter: agFilterNames.set,
              minWidth: 120,
              hide: false,
              pinned: null,
              pivot: false,
              pivotIndex: null,
              rowGroup: false,
              rowGroupIndex: null,
              sortIndex: null,
              aggFunc: null,
              headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
            }

          default:
            return {}
        }
      });
      return result;
    }
}
